import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import API from '../Api/api';
import './SearchSuggest.css';

const MAX_SUGGESTIONS = 5;
const BOX_ANCHOR_SELECTOR = '.search-box, .fp-search-wrap';

const resolveCompanyId = (item) => {
  if (!item || item.type !== 'company') return null;
  const raw = item.id ?? item.company_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Shared typeahead for Home + Properties filter search.
 *
 * attached=true  → dropdown is a child of the search box (connected UI)
 * attached=false → fixed portal (avoids clipping inside scrollable filter panels)
 */
const SearchSuggest = ({
  value = '',
  onChange,
  onSubmit,
  placeholder = 'Search location, type, amenity…',
  className = '',
  inputClassName = '',
  showClear = true,
  autoFocus = false,
  minChars = 1,
  attached = false,
}) => {
  const navigate = useNavigate();
  const [localValue, setLocalValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [panelStyle, setPanelStyle] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const timerRef = useRef(null);
  const skipFetch = useRef(false);
  const reqId = useRef(0);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const getAnchorEl = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return null;
    // Prefer the full search box (icon + input + button), not just .ss-wrap
    return wrap.closest(BOX_ANCHOR_SELECTOR) || wrap;
  }, []);

  useLayoutEffect(() => {
    setAnchorEl(getAnchorEl());
  }, [getAnchorEl]);

  const updatePanelPosition = useCallback(() => {
    const anchor = getAnchorEl();
    if (!anchor) return;

    if (attached) {
      // Layout is CSS-driven (absolute under the search box)
      setPanelStyle(null);
      return;
    }

    const r = anchor.getBoundingClientRect();
    const gap = 8;
    const viewportPad = 12;
    const isFilter = !!anchor.closest('.fp-search-wrap');
    const minWidth = isFilter ? 360 : 560;
    const preferred = Math.max(r.width, minWidth);
    const maxWidth = Math.max(200, window.innerWidth - viewportPad * 2);
    const width = Math.min(preferred, maxWidth);
    let left = r.left;
    if (left + width > window.innerWidth - viewportPad) {
      left = Math.max(viewportPad, window.innerWidth - viewportPad - width);
    }
    if (left < viewportPad) left = viewportPad;
    const maxHeight = Math.min(480, Math.max(180, window.innerHeight - r.bottom - gap - viewportPad));
    setPanelStyle({
      position: 'fixed',
      top: Math.round(r.bottom + gap),
      left: Math.round(left),
      width: Math.round(width),
      maxHeight,
      zIndex: 10050,
    });
  }, [getAnchorEl, attached]);

  const showPanel = open && (loading || suggestions.length > 0 || localValue.trim().length >= minChars);

  // Toggle connected look on the search box while open
  useEffect(() => {
    const anchor = getAnchorEl();
    if (!anchor || !attached) return undefined;
    anchor.classList.toggle('ss-anchor-open', !!showPanel);
    return () => {
      anchor.classList.remove('ss-anchor-open');
    };
  }, [showPanel, attached, getAnchorEl]);

  useLayoutEffect(() => {
    if (!open || attached) return undefined;
    updatePanelPosition();
    const onWin = () => updatePanelPosition();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open, suggestions.length, loading, updatePanelPosition, attached]);

  useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      const anchor = getAnchorEl();
      if (anchor?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [getAnchorEl]);

  const fetchSuggestions = useCallback(async (q) => {
    const trimmed = String(q || '').trim();
    if (trimmed.length < minChars) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    const id = ++reqId.current;
    setLoading(true);
    setOpen(true);
    try {
      const res = await API.get('/property/suggest', {
        params: { q: trimmed, limit: MAX_SUGGESTIONS, status: 2 },
      });
      if (id !== reqId.current) return;
      const list = Array.isArray(res.data?.suggestions) ? res.data.suggestions : [];
      setSuggestions(list.slice(0, MAX_SUGGESTIONS));
      setActiveIdx(-1);
    } catch {
      if (id !== reqId.current) return;
      setSuggestions([]);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [minChars]);

  const handleInputChange = (e) => {
    const next = e.target.value;
    setLocalValue(next);
    onChange?.(next);
    if (skipFetch.current) {
      skipFetch.current = false;
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(next), 250);
  };

  const applySuggestion = (item) => {
    const companyId = resolveCompanyId(item);
    // Company rows always open the Company Profile page — do not update search
    // state first (that can race and send the user to /properties instead).
    if (companyId) {
      skipFetch.current = true;
      setSuggestions([]);
      setOpen(false);
      navigate(`/company/${companyId}`);
      return;
    }

    const next = item?.value || item?.label || '';
    skipFetch.current = true;
    setLocalValue(next);
    onChange?.(next);
    setSuggestions([]);
    setOpen(false);
    onSubmit?.(next, item);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open && suggestions.length) setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        applySuggestion(suggestions[activeIdx]);
      } else {
        setOpen(false);
        onSubmit?.(localValue.trim());
      }
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const clear = () => {
    setLocalValue('');
    onChange?.('');
    setSuggestions([]);
    setOpen(false);
  };

  const handleBlur = (e) => {
    const next = e.relatedTarget;
    if (panelRef.current?.contains(next)) return;
    if (getAnchorEl()?.contains(next)) return;
    // Delay so option mousedown can fire first
    window.setTimeout(() => {
      if (panelRef.current?.matches(':hover')) return;
      setOpen(false);
    }, 120);
  };

  const dropdownInner = (
    <div className="ss-dropdown-body">
      {loading && suggestions.length === 0 && (
        <div className="ss-empty">Searching…</div>
      )}

      {!loading && suggestions.length === 0 && (
        <div className="ss-empty">No matches — press Enter to search</div>
      )}

      {suggestions.length > 0 && (
        <ul className="ss-list">
          {suggestions.map((item, idx) => {
            const chips = Array.isArray(item.attributes) ? item.attributes.slice(0, 6) : [];
            const isCompany = item.type === 'company';
            const pid = !isCompany && item.property_id != null ? `#${item.property_id}` : null;
            const initials = String(item.label || item.value || '?').slice(0, 2).toUpperCase();
            return (
              <li
                key={`${item.type}-${item.property_id || item.id || item.name_key || item.value}-${idx}`}
                role="option"
                aria-selected={idx === activeIdx}
                className={`ss-card ${isCompany ? 'ss-card--company' : ''} ${idx === activeIdx ? 'is-active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  applySuggestion(item);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  applySuggestion(item);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
              >
                <div className={`ss-card-media${isCompany ? ' ss-card-media--company' : ''}`}>
                  {item.image ? (
                    <img
                      src={item.image}
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement?.classList.add('is-empty');
                      }}
                    />
                  ) : isCompany ? (
                    <span className="ss-card-initials" aria-hidden="true">{initials}</span>
                  ) : (
                    <span className="ss-card-ph" aria-hidden="true" />
                  )}
                </div>

                <div className="ss-card-content">
                  <div className="ss-card-top">
                    <p className="ss-card-title">{item.label}</p>
                    {pid && <span className="ss-card-id">{pid}</span>}
                  </div>

                  {!isCompany && (item.city || item.category || item.sublabel) && (
                    <p className="ss-card-meta">
                      {[item.city, item.category].filter(Boolean).join(' · ') || item.sublabel}
                    </p>
                  )}

                  {!isCompany && (item.bhk || item.price || chips.length > 0) && (
                    <div className="ss-card-tags">
                      {item.bhk && (
                        <span className="ss-tag ss-tag--accent">{item.bhk}</span>
                      )}
                      {item.price && (
                        <span className="ss-tag ss-tag--price">{item.price}</span>
                      )}
                      {chips
                        .filter((c) => c.key !== 'bhk' && c.key !== 'price' && c.key !== 'property_id')
                        .slice(0, 3)
                        .map((chip) => (
                          <span key={`${chip.key}-${chip.value}`} className="ss-tag">
                            {chip.value}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  let dropdown = null;
  if (showPanel) {
    if (attached && anchorEl) {
      dropdown = createPortal(
        <div
          ref={panelRef}
          className="ss-dropdown ss-dropdown--attached"
          role="listbox"
        >
          {dropdownInner}
        </div>,
        anchorEl,
      );
    } else if (!attached && panelStyle) {
      dropdown = createPortal(
        <div
          ref={panelRef}
          className="ss-dropdown ss-dropdown--portal"
          style={panelStyle}
          role="listbox"
        >
          {dropdownInner}
        </div>,
        document.body,
      );
    }
  }

  return (
    <div className={`ss-wrap ${attached ? 'ss-wrap--attached' : ''} ${className}`} ref={wrapRef}>
      <div className="ss-input-row">
        <input
          type="text"
          className={inputClassName}
          placeholder={placeholder}
          value={localValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            updatePanelPosition();
            const q = localValue.trim();
            if (q.length >= minChars) {
              if (suggestions.length) setOpen(true);
              else fetchSuggestions(localValue);
            }
          }}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          autoComplete="off"
          role="combobox"
          aria-expanded={!!showPanel}
          aria-autocomplete="list"
        />
        {showClear && localValue && (
          <button type="button" className="ss-clear" onClick={clear} aria-label="Clear search">
            ×
          </button>
        )}
      </div>
      {dropdown}
    </div>
  );
};

export default SearchSuggest;
